import type { ShopCard } from '@croissants/contracts';
import {
  pickupHoursLabel,
  type WeeklyHoursGroup,
  weeklyHoursGroups,
} from '@croissants/contracts/projections';
import { dateTag, formatList, type Locale } from '@croissants/i18n';

/**
 * The two facts a mode B tile states about the HOUSE rather than about a
 * morning: how far it is, and the week it keeps (GST-030).
 *
 * Both arrive derived - the distance is allocation's own figure and the week is
 * `weeklyPickupHours` on the server - so what is decided here is only how they
 * READ, in the guest's language.
 */

/**
 * `450 m` under a kilometre, `1.2 km` / `1,2 km` from there.
 *
 * Rounded to 50 m: the figure is a straight line to the nearest counter, and
 * `437 m` claims a precision a guest walking round a Paris block would not
 * recognise. Never `0 m` - a house in the same building is 50 m away.
 */
export function distanceLabel(meters: number, locale: Locale): string {
  const rounded = Math.max(50, Math.round(meters / 50) * 50);
  if (rounded < 1000) return `${rounded} m`;
  // From the RAW figure: rounding 1234 to 1250 first and then to a decimal
  // printed 1.3 km for a house 1.2 km away.
  const km = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.max(meters, 1000) / 1000);
  return `${km} km`;
}

/** `Mon` / `lun` - the locale's own short name, without French's trailing point. */
function weekdayName(weekday: number, locale: Locale): string {
  // 7 January 2024 was a Sunday, so day 7 + n falls on weekday n.
  const name = new Intl.DateTimeFormat(dateTag(locale), {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2024, 0, 7 + weekday)));
  return name.replace(/\.$/, '');
}

function daysOf(group: WeeklyHoursGroup, locale: Locale): string {
  const from = weekdayName(group.from, locale);
  return group.from === group.to ? from : `${from}-${weekdayName(group.to, locale)}`;
}

export interface WeeklyHoursWords {
  /** "Every day" */
  readonly everyDay: string;
  /** "closed {days}" -> the finished phrase. */
  readonly closed: (days: string) => string;
}

/**
 * The week as a few short phrases, in reading order:
 * `Tue-Sat 07:00-20:00`, `Sun 08:00-13:00`, `closed Mon`.
 *
 * Open runs first, Monday first; the shut days are gathered into ONE phrase at
 * the end, because "closed Mon" is the exception a guest scans for and it reads
 * as one however the days fall. A house keeping one span all week is
 * `Every day 07:00-20:00`. Each phrase is returned on its own so the tile can
 * keep it unbroken and wrap BETWEEN phrases. Empty when the week says nothing
 * - shut all seven days, or malformed.
 */
export function weeklyHoursPhrases(
  week: ShopCard['weeklyHours'],
  locale: Locale,
  words: WeeklyHoursWords,
): readonly string[] {
  const groups = weeklyHoursGroups(week);
  const phrases: string[] = [];
  const shut: string[] = [];
  for (const group of groups) {
    if (group.hours === null) shut.push(daysOf(group, locale));
    else phrases.push(`${daysOf(group, locale)} ${pickupHoursLabel(group.hours)}`);
  }
  if (phrases.length === 0) return [];

  const [only] = groups;
  if (groups.length === 1 && only !== undefined && only.hours !== null) {
    return [`${words.everyDay} ${pickupHoursLabel(only.hours)}`];
  }

  if (shut.length > 0) phrases.push(words.closed(shut.join(', ')));
  return phrases;
}

/**
 * `16:30-19:30` that a narrow tile cannot break at its hyphen: a WORD JOINER on
 * each side of it. The line wraps between words, and a range split over two
 * lines reads as two times rather than one span.
 */
function unbrokenRange(start: string, end: string): string {
  return `${start}\u2060-\u2060${end}`;
}

export interface DeliveryLineWords {
  /** "Delivery today {times}" */
  readonly today: (times: string) => string;
  /** "Delivery tomorrow {times}" */
  readonly tomorrow: (times: string) => string;
  /** "Delivery {day} {times}" - a named weekday. */
  readonly later: (day: string, times: string) => string;
  /** "until {time}" */
  readonly until: (time: string) => string;
  /** "Bakery closed, reopens {date}" */
  readonly closedUntil: (date: string) => string;
}

/**
 * The tile's last line: when this house delivers on the first morning it can
 * be ordered for (GST-031, founder 2026-09-18).
 *
 *   open now, straight through   Delivery today until 13:00
 *   open now, two services       Delivery today until 13:00 and 16:30-19:30
 *   today, not open yet          Delivery today 07:45-13:00
 *   tomorrow / a later morning   Delivery tomorrow 07:00-13:00
 *
 * A guest who can order NOW is up against the closing time, so that is what
 * the first service states; anybody else needs the whole range. The times are
 * the HOUSE'S OWN opening hours for that day (`deliverySpans`,
 * `pickupServicesOn` on the server) - the closing time is the one the baker
 * typed, never a delivery window's end, which runs past it. Which day it is
 * comes from the platform's clock (`nextAvailableDayKind`) - nothing here
 * reads the device's. Null when there is nothing to state, so the tile falls
 * back to the bare date.
 *
 * A house CLOSED on the asked morning (`closure`) says so instead, and when it
 * reopens: `Bakery closed, reopens Monday 26 Oct` (day-first, as every English
 * date here is).
 */
export function deliveryLine(
  shop: Pick<
    ShopCard,
    | 'deliverySpans'
    | 'orderableNow'
    | 'nextAvailableDayKind'
    | 'nextAvailableServiceDay'
    | 'closure'
  >,
  locale: Locale,
  words: DeliveryLineWords,
): string | null {
  if (shop.nextAvailableServiceDay === null) return null;
  // Midday UTC keeps the calendar date stable, as `serviceDayLabel` does.
  const midday = new Date(`${shop.nextAvailableServiceDay}T12:00:00.000Z`);
  // `?? null` is the deploy window, as on the tile: an older worker sends none.
  if ((shop.closure ?? null) !== null) {
    const date = new Intl.DateTimeFormat(dateTag(locale), {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(midday);
    return words.closedUntil(date);
  }
  const spans = shop.deliverySpans ?? [];
  if (spans.length === 0) return null;

  const openNow = shop.nextAvailableDayKind === 'TODAY' && shop.orderableNow;
  const times = formatList(
    spans.map((span, index) => {
      // No closing time configured: the opening is all there is to state.
      if (span.end === null) return span.start;
      return index === 0 && openNow ? words.until(span.end) : unbrokenRange(span.start, span.end);
    }),
    locale,
  );

  if (shop.nextAvailableDayKind === 'TODAY') return words.today(times);
  if (shop.nextAvailableDayKind === 'TOMORROW') return words.tomorrow(times);
  const day = new Intl.DateTimeFormat(dateTag(locale), {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(midday);
  return words.later(day, times);
}
